import { Platform, StyleSheet } from 'react-native';
import { spacing, ticketColors, ticketFonts, typography } from '../theme/public-api';

/** Patrones visuales compartidos para modales (historial, listas, formularios, confirmación). */
export const modalExperience = StyleSheet.create({
  modalRoot: {
    alignItems: 'center',
    ...(Platform.OS === 'web'
      ? ({
          width: '100%',
          // maxHeight explícita: sin tope el root crece con el documento y los
          // maxHeight % de la card no resuelven (padre con altura indefinida →
          // el footer del modal queda fuera del viewport).
          minHeight: '100vh' as unknown as number,
          maxHeight: '100vh' as unknown as number,
        } as const)
      : {}),
  },
  modalWidthStandard: {
    width: '100%',
    maxWidth: 540,
    alignSelf: 'center',
  },
  modalWidthComfortable: {
    width: '100%',
    maxWidth: 640,
    alignSelf: 'center',
  },
  /** Formularios con grilla 2-col (facturas, carga de stock). */
  modalWidthBill: {
    width: '100%',
    maxWidth: 860,
    alignSelf: 'center',
  },
  modalWidthWide: {
    width: '100%',
    maxWidth: 1200,
    alignSelf: 'center',
  },
  modalScrollContent: {
    flexGrow: 1,
    justifyContent: 'center',
    paddingVertical: spacing.md,
  },
  heroBand: {
    borderRadius: 14,
    borderWidth: 1,
    borderColor: ticketColors.ruleSoft,
    backgroundColor: ticketColors.paper,
    padding: spacing.md,
    flexDirection: 'row',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    gap: spacing.sm,
  },
  heroRow: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    gap: spacing.sm,
  },
  heroText: {
    flex: 1,
    minWidth: 0,
    gap: 2,
  },
  heroKicker: {
    fontSize: 10,
    color: ticketColors.ink3,
    fontWeight: '700',
    letterSpacing: 0.6,
    textTransform: 'uppercase',
    fontFamily: ticketFonts.mono ?? ticketFonts.sans,
  },
  heroTitle: {
    fontSize: 17,
    color: ticketColors.ink,
    fontWeight: '800',
    letterSpacing: -0.3,
    lineHeight: 22,
    fontFamily: ticketFonts.sans,
  },
  heroSubtitle: {
    fontSize: 13,
    color: ticketColors.ink2,
    fontWeight: '500',
    lineHeight: 18,
    fontFamily: ticketFonts.sans,
  },
  countBadge: {
    minWidth: 70,
    borderRadius: 12,
    borderWidth: 1,
    borderColor: ticketColors.brandSoft,
    backgroundColor: ticketColors.brandSoft,
    alignItems: 'center',
    paddingHorizontal: spacing.sm,
    paddingVertical: spacing.xs,
  },
  countValue: {
    ...typography.titleLg,
    color: ticketColors.gold,
    fontWeight: '900',
    fontFamily: ticketFonts.sans,
  },
  countLabel: {
    ...typography.caption,
    color: ticketColors.ink2,
    fontWeight: '700',
    fontFamily: ticketFonts.sans,
  },
  scopeHint: {
    marginTop: spacing.xs,
    opacity: 0.92,
  },
  filtersPanel: {
    marginTop: spacing.sm,
    borderRadius: 14,
    borderWidth: 1,
    borderColor: ticketColors.ruleSoft,
    backgroundColor: ticketColors.paper,
    padding: spacing.md,
    gap: spacing.sm,
  },
  filtersPanelTitle: {
    ...typography.caption,
    color: ticketColors.ink3,
    fontWeight: '800',
    letterSpacing: 0.35,
    textTransform: 'uppercase',
    fontFamily: ticketFonts.sans,
  },
  filterFieldLabel: {
    ...typography.caption,
    color: ticketColors.ink2,
    fontWeight: '600',
    fontFamily: ticketFonts.sans,
  },
  filterDateRow: {
    flexDirection: 'row',
    gap: spacing.sm,
    alignItems: 'flex-start',
  },
  filterDateCol: {
    flex: 1,
    minWidth: 0,
    gap: spacing.xs,
  },
  filterDateHint: {
    ...typography.caption,
    color: ticketColors.ink3,
    fontFamily: ticketFonts.sans,
  },
  filterSelectTrigger: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: spacing.sm,
    borderWidth: 1,
    borderColor: ticketColors.ruleSoft,
    borderRadius: 10,
    backgroundColor: ticketColors.paper,
    paddingHorizontal: spacing.md,
    paddingVertical: spacing.sm,
    minHeight: 44,
  },
  filterSelectTriggerActive: {
    borderColor: ticketColors.gold,
    backgroundColor: ticketColors.brandSoft,
  },
  filterSelectMain: {
    flex: 1,
    minWidth: 0,
    gap: 2,
  },
  filterSelectValue: {
    ...typography.body,
    color: ticketColors.ink,
    fontWeight: '700',
    fontFamily: ticketFonts.sans,
  },
  filterSelectHint: {
    ...typography.caption,
    color: ticketColors.ink3,
    fontFamily: ticketFonts.sans,
  },
  filterActions: {
    flexDirection: 'row',
    gap: spacing.sm,
    alignItems: 'stretch',
    marginTop: spacing.xs,
  },
  filterActionFlex: {
    flex: 1,
    minWidth: 0,
  },
  stateBox: {
    marginVertical: spacing.sm,
    padding: spacing.lg,
    borderRadius: 14,
    borderWidth: 1,
    borderColor: ticketColors.ruleSoft,
    backgroundColor: ticketColors.paper,
    alignItems: 'center',
    gap: spacing.sm,
  },
  stateTitle: {
    ...typography.body,
    color: ticketColors.ink,
    fontWeight: '700',
    fontFamily: ticketFonts.sans,
  },
  stateText: {
    ...typography.caption,
    color: ticketColors.ink2,
    textAlign: 'center',
    lineHeight: 20,
    fontFamily: ticketFonts.sans,
  },
  listContainer: {
    marginVertical: spacing.sm,
    minHeight: 120,
    borderRadius: 14,
    borderWidth: 1,
    borderColor: ticketColors.ruleSoft,
    backgroundColor: ticketColors.paper,
    overflow: 'hidden',
  },
  listContainerPlain: {
    marginVertical: spacing.sm,
    minHeight: 120,
  },
  listRow: {
    paddingHorizontal: spacing.md,
    paddingVertical: spacing.md,
    gap: spacing.sm,
    borderBottomWidth: StyleSheet.hairlineWidth,
    borderBottomColor: ticketColors.rule,
    overflow: 'hidden',
    width: '100%',
  },
  listRowTitle: {
    ...typography.body,
    color: ticketColors.ink,
    fontWeight: '700',
    fontFamily: ticketFonts.sans,
  },
  listMeta: {
    ...typography.caption,
    color: ticketColors.ink2,
    lineHeight: 18,
    fontFamily: ticketFonts.sans,
  },
  listMetaMuted: {
    ...typography.caption,
    color: ticketColors.ink3,
    fontFamily: ticketFonts.sans,
  },
  accentLink: {
    ...typography.caption,
    color: ticketColors.gold,
    fontWeight: '700',
    fontFamily: ticketFonts.sans,
  },
  footerLoading: {
    paddingVertical: spacing.md,
    alignItems: 'center',
    gap: spacing.xs,
  },
  loadMoreBtn: {
    paddingVertical: spacing.md,
    alignItems: 'center',
  },
  closeBtn: {
    marginTop: spacing.xs,
  },
  pickerBackdrop: {
    flex: 1,
    backgroundColor: ticketColors.dim,
    justifyContent: 'center',
    alignItems: 'center',
    padding: spacing.lg,
    ...(Platform.OS === 'web'
      ? ({
          width: '100%',
          minHeight: '100vh' as unknown as number,
        } as const)
      : {}),
  },
  pickerCard: {
    width: '100%',
    maxWidth: 420,
    alignSelf: 'center',
    backgroundColor: ticketColors.paper,
    borderRadius: 14,
    borderWidth: 1,
    borderColor: ticketColors.ruleSoft,
    padding: spacing.md,
    maxHeight: '70%',
    gap: spacing.sm,
  },
  pickerTitle: {
    ...typography.body,
    color: ticketColors.ink,
    fontWeight: '800',
    fontFamily: ticketFonts.sans,
  },
  pickerScroll: {
    maxHeight: 320,
  },
  pickerRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: spacing.sm,
    paddingVertical: spacing.sm,
    paddingHorizontal: spacing.xs,
    borderBottomWidth: StyleSheet.hairlineWidth,
    borderBottomColor: ticketColors.rule,
  },
  pickerRowActive: {
    backgroundColor: ticketColors.brandSoft,
    borderRadius: 8,
  },
  pickerRowText: {
    ...typography.body,
    color: ticketColors.ink,
    flex: 1,
    fontFamily: ticketFonts.sans,
  },
  pickerRowTextActive: {
    color: ticketColors.gold,
    fontWeight: '700',
    fontFamily: ticketFonts.sans,
  },
  warningPanel: {
    borderRadius: 10,
    borderWidth: 1,
    borderColor: ticketColors.warnBorder,
    backgroundColor: ticketColors.warnBg,
    padding: spacing.md,
    gap: spacing.xs,
  },
  /** Aviso de inventario en POS (ámbar suave; no borde duro en todo el modal). */
  stockNoticePanel: {
    borderRadius: 10,
    borderWidth: 1,
    borderColor: ticketColors.brandSoft,
    backgroundColor: ticketColors.brandSoft,
    padding: spacing.md,
  },
  stockNoticePanelBlocked: {
    borderRadius: 10,
    borderWidth: 1,
    borderColor: ticketColors.ruleSoft,
    backgroundColor: ticketColors.paper2,
    padding: spacing.md,
  },
  stockNoticeBody: {
    ...typography.body,
    color: ticketColors.ink2,
    lineHeight: 22,
    fontFamily: ticketFonts.sans,
  },
  stockNoticeHero: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: spacing.md,
  },
  stockNoticeIconWrap: {
    width: 44,
    height: 44,
    borderRadius: 10,
    borderWidth: 1,
    borderColor: ticketColors.brandSoft,
    backgroundColor: ticketColors.brandSoft,
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  },
  stockNoticeIconWrapBlocked: {
    borderColor: 'rgba(239, 68, 68, 0.35)',
    backgroundColor: 'rgba(239, 68, 68, 0.1)',
  },
  stockNoticeHeroText: {
    flex: 1,
    minWidth: 0,
    gap: 2,
    paddingTop: 2,
  },
  stockNoticeKicker: {
    ...typography.caption,
    color: ticketColors.ink3,
    fontWeight: '800',
    letterSpacing: 0.4,
    textTransform: 'uppercase',
    fontFamily: ticketFonts.sans,
  },
  stockNoticeTitle: {
    ...typography.titleXL,
    color: ticketColors.ink,
    fontWeight: '800',
    lineHeight: 30,
    fontFamily: ticketFonts.sans,
  },
  stockNoticeActions: {
    gap: spacing.sm,
    width: '100%',
  },
  stockNoticePrimaryAction: {
    alignSelf: 'stretch',
    minHeight: 52,
    justifyContent: 'center',
  },
  stockNoticeSecondaryRow: {
    flexDirection: 'row',
    alignItems: 'stretch',
    gap: spacing.sm,
    width: '100%',
    
    
  },
  /** Cuerpo de confirmación: icono + texto en un solo panel (evita caja vacía + panel duplicado). */
  confirmBodyPanel: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: spacing.md,
    borderRadius: 14,
    borderWidth: 1,
    borderColor: ticketColors.ruleSoft,
    backgroundColor: ticketColors.paper2,
    padding: spacing.md,
  },
  confirmBodyPanelDanger: {
    borderColor: ticketColors.warnBorder,
    backgroundColor: ticketColors.warnBg,
  },
  confirmBodyText: {
    flex: 1,
    minWidth: 0,
    gap: spacing.xs,
    paddingTop: 2,
  },
  confirmBodyTitle: {
    ...typography.body,
    color: ticketColors.ink,
    fontWeight: '800',
    lineHeight: 22,
    fontFamily: ticketFonts.sans,
  },
  confirmBodyMessage: {
    ...typography.body,
    color: ticketColors.ink2,
    lineHeight: 22,
    fontFamily: ticketFonts.sans,
  },
  confirmBodyMessageDanger: {
    color: ticketColors.ink,
    fontWeight: '600',
    fontFamily: ticketFonts.sans,
  },
  confirmIcon: {
    width: 44,
    height: 44,
    borderRadius: 10,
    borderWidth: 1,
    borderColor: ticketColors.brandSoft,
    backgroundColor: ticketColors.brandSoft,
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  },
  confirmIconDanger: {
    borderColor: ticketColors.warnBorder,
    backgroundColor: ticketColors.warnBg,
  },
  confirmOverlayRoot: {
    ...StyleSheet.absoluteFill,
    zIndex: 9999,
    elevation: 9999,
    justifyContent: 'center',
    alignItems: 'center',
    padding: spacing.lg,
  },
  confirmOverlayBackdrop: {
    ...StyleSheet.absoluteFill,
    backgroundColor: ticketColors.dim,
  },
  /** Gap between sibling bordered blocks (hero + formPanel, formPanel + infoPanel). */
  formStack: {
    gap: spacing.lg,
  },
  /**
   * Form content container per modal (or per step). Transparent: sections sit
   * directly on the white modal surface — do not wrap forms in a bordered card.
   */
  formPanel: {
    padding: spacing.md,
    gap: spacing.md,
  },
  formSection: {
    gap: spacing.md,
  },
  formSectionWithDivider: {
    gap: spacing.md,
    paddingTop: spacing.md,
    borderTopWidth: StyleSheet.hairlineWidth,
    borderTopColor: ticketColors.rule,
  },
  formSectionTitle: {
    ...typography.caption,
    color: ticketColors.ink3,
    fontWeight: '700',
    letterSpacing: 0.4,
    textTransform: 'uppercase',
    fontFamily: ticketFonts.sans,
  },
  formSectionHelper: {
    ...typography.caption,
    color: ticketColors.ink2,
    lineHeight: 18,
  },
  formField: {
    gap: spacing.xs,
  },
  formLabelRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.xs,
  },
  formLabel: {
    fontSize: 13,
    color: ticketColors.ink3,
    fontWeight: '600',
    fontFamily: ticketFonts.sans,
  },
  /** Requerido = «*» rojo, misma convención que el form de catálogo. */
  requiredBadge: {
    color: ticketColors.bad,
    fontWeight: '700',
    fontSize: 13,
    fontFamily: ticketFonts.sans,
  },
  formInput: {
    borderWidth: 1,
    borderColor: ticketColors.rule,
    borderRadius: 10,
    paddingHorizontal: spacing.md,
    paddingVertical: Platform.OS === 'ios' ? 12 : 10,
    color: ticketColors.ink,
    backgroundColor: ticketColors.paper,
    fontSize: 16,
    fontWeight: '500',
    fontFamily: ticketFonts.sans,
    minHeight: 44,
  },
  formInputMultiline: {
    minHeight: 88,
    textAlignVertical: 'top',
  },
  entitySelector: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.sm,
    borderWidth: 1,
    borderColor: ticketColors.rule,
    borderRadius: 10,
    paddingHorizontal: spacing.md,
    paddingVertical: spacing.sm,
    backgroundColor: ticketColors.paper,
    minHeight: 44,
  },
  entitySelectorActive: {
    borderColor: ticketColors.gold,
    backgroundColor: ticketColors.brandSoft,
  },
  pillBadge: {
    borderRadius: 999,
    borderWidth: 1,
    borderColor: ticketColors.gold,
    backgroundColor: ticketColors.gold,
    paddingHorizontal: spacing.sm,
    paddingVertical: 5,
    alignSelf: 'flex-start',
  },
  pillBadgeText: {
    ...typography.caption,
    color: ticketColors.goldInk,
    fontWeight: '800',
    fontFamily: ticketFonts.sans,
  },
  infoPanel: {
    borderRadius: 14,
    borderWidth: 1,
    borderColor: ticketColors.ruleSoft,
    backgroundColor: ticketColors.paper2,
    padding: spacing.md,
    gap: spacing.xs,
  },
  checkboxRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.sm,
  },
  checkbox: {
    width: 22,
    height: 22,
    borderRadius: 6,
    borderWidth: 2,
    borderColor: ticketColors.ruleSoft,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: ticketColors.paper,
  },
  checkboxChecked: {
    backgroundColor: ticketColors.gold,
    borderColor: ticketColors.gold,
  },
  checkboxLabel: {
    ...typography.caption,
    color: ticketColors.ink2,
    flex: 1,
    lineHeight: 18,
    fontFamily: ticketFonts.sans,
  },
  footerActions: {
    marginTop: spacing.sm,
    width: '100%',
    flexDirection: 'row',
    gap: spacing.sm,
  },
  dangerButton: {
    borderRadius: 10,
    borderWidth: 1,
    borderColor: ticketColors.warnBorder,
    backgroundColor: ticketColors.warnBg,
    paddingVertical: spacing.sm,
    paddingHorizontal: spacing.md,
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: 44,
  },
  dangerButtonText: {
    ...typography.body,
    color: '#B45309',
    fontWeight: '700',
    fontFamily: ticketFonts.sans,
  },
  detailScroll: {
    maxHeight: 360,
    marginVertical: spacing.sm,
  },
  detailMeta: {
    ...typography.body,
    color: ticketColors.ink,
    marginBottom: spacing.xs,
    fontFamily: ticketFonts.sans,
  },
  detailMetaSecondary: {
    ...typography.caption,
    color: ticketColors.ink2,
    lineHeight: 18,
    marginBottom: spacing.xs,
    fontFamily: ticketFonts.sans,
  },
  detailSectionTitle: {
    ...typography.body,
    color: ticketColors.ink,
    fontWeight: '700',
    marginTop: spacing.md,
    marginBottom: spacing.xs,
    fontFamily: ticketFonts.sans,
  },
  detailBlockTitle: {
    fontSize: 14,
    color: ticketColors.ink,
    fontWeight: '800',
    fontFamily: ticketFonts.sans,
  },
  detailInfoLine: {
    fontSize: 13,
    color: ticketColors.ink2,
    fontWeight: '600',
    lineHeight: 18,
    fontFamily: ticketFonts.sans,
  },
  detailMetricHero: {
    borderRadius: 14,
    borderWidth: 1,
    borderColor: ticketColors.ruleSoft,
    backgroundColor: ticketColors.paper2,
    padding: spacing.md,
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    gap: spacing.sm,
  },
  detailMetricMain: {
    flex: 1,
    minWidth: 0,
    gap: 2,
  },
  detailMetricLabel: {
    ...typography.caption,
    color: ticketColors.ink3,
    fontWeight: '800',
    textTransform: 'uppercase',
    fontSize: 11,
    letterSpacing: 0.4,
    fontFamily: ticketFonts.sans,
  },
  detailMetricValue: {
    fontSize: 28,
    color: ticketColors.ink,
    fontWeight: '900',
    lineHeight: 34,
    fontFamily: ticketFonts.sans,
  },
  detailMetricMeta: {
    ...typography.caption,
    color: ticketColors.ink2,
    fontWeight: '600',
    fontFamily: ticketFonts.sans,
  },
  detailMetaGrid: {
    padding: spacing.md,
    gap: spacing.sm,
  },
  /** Sección plana dentro de un detalle (proto .sec-divided): sin card, hairline arriba. */
  detailFlatSection: {
    gap: spacing.xs,
    paddingTop: spacing.lg,
    borderTopWidth: StyleSheet.hairlineWidth,
    borderTopColor: ticketColors.ruleSoft,
  },
  detailMetaRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    gap: spacing.md,
  },
  detailMetaLabel: {
    ...typography.caption,
    color: ticketColors.ink3,
    fontWeight: '600',
    fontFamily: ticketFonts.sans,
  },
  detailMetaValue: {
    ...typography.body,
    color: ticketColors.ink,
    fontWeight: '700',
    textAlign: 'right',
    flex: 1,
    fontFamily: ticketFonts.sans,
  },
  detailMetaValueDanger: {
    color: ticketColors.bad,
  },
  detailKvRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: spacing.md,
    paddingVertical: 4,
  },
  detailKvValueStrong: {
    fontSize: 14,
    color: ticketColors.ink,
    fontWeight: '700',
    textAlign: 'right',
    fontFamily: ticketFonts.sans,
  },
  detailStatusInlineOk: {
    color: ticketColors.ink3,
  },
  detailStatusInlineWarn: {
    color: ticketColors.gold,
    fontWeight: '700',
  },
  detailStatusInlineCritical: {
    color: ticketColors.bad,
    fontWeight: '700',
  },
  detailActionsPanel: {
    gap: spacing.sm,
    paddingTop: spacing.md,
  },
  detailActionsGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: spacing.sm,
  },
  detailActionBtn: {
    flexGrow: 1,
    flexBasis: '46%',
    minWidth: 0,
    minHeight: 44,
    borderRadius: 10,
    borderWidth: 1,
    borderColor: ticketColors.ruleSoft,
    backgroundColor: ticketColors.paper,
    paddingVertical: spacing.sm,
    paddingHorizontal: spacing.md,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: spacing.xs,
  },
  detailActionBtnPrimary: {
    backgroundColor: ticketColors.gold,
    borderColor: ticketColors.gold,
  },
  detailActionBtnDanger: {
    borderColor: ticketColors.warnBorder,
    backgroundColor: ticketColors.warnBg,
  },
  detailActionText: {
    fontSize: 14,
    color: ticketColors.ink,
    fontWeight: '700',
    fontFamily: ticketFonts.sans,
  },
  detailActionTextPrimary: {
    color: '#FFFFFF',
    fontWeight: '800',
  },
  detailActionTextDanger: {
    color: '#B45309',
    fontWeight: '700',
  },
  statusPill: {
    alignSelf: 'flex-start',
    borderRadius: 999,
    borderWidth: 1,
    paddingVertical: 5,
    paddingHorizontal: spacing.sm,
  },
  statusPillOk: {
    borderColor: 'rgba(34, 197, 94, 0.5)',
    backgroundColor: 'rgba(34, 197, 94, 0.1)',
  },
  statusPillWarning: {
    borderColor: 'rgba(245, 158, 11, 0.58)',
    backgroundColor: 'rgba(245, 158, 11, 0.09)',
  },
  statusPillCritical: {
    borderColor: 'rgba(239, 68, 68, 0.5)',
    backgroundColor: 'rgba(239, 68, 68, 0.1)',
  },
  statusPillTextOk: {
    fontSize: 10,
    fontWeight: '800',
    color: ticketColors.ok,
    textTransform: 'uppercase',
    letterSpacing: 0.3,
    fontFamily: ticketFonts.sans,
  },
  statusPillTextWarning: {
    fontSize: 10,
    fontWeight: '800',
    color: ticketColors.gold,
    textTransform: 'uppercase',
    letterSpacing: 0.3,
    fontFamily: ticketFonts.sans,
  },
  statusPillTextCritical: {
    fontSize: 10,
    fontWeight: '800',
    color: ticketColors.bad,
    textTransform: 'uppercase',
    letterSpacing: 0.3,
    fontFamily: ticketFonts.sans,
  },
  errorBanner: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: spacing.sm,
    padding: spacing.sm + 2,
    borderRadius: 10,
    borderWidth: 1,
    borderColor: ticketColors.bad,
    backgroundColor: 'rgba(239, 68, 68, 0.08)',
  },
  errorBannerText: {
    flex: 1,
    fontSize: 13,
    lineHeight: 18,
    color: ticketColors.bad,
    fontWeight: '500',
    fontFamily: ticketFonts.sans,
  },
});
